import ArrowRightNew from "assets/website/icons/ArrowRightNew";
import type { EventData } from "../data";
import { lines } from "assets/website";
import { getLumaEventId, useLumaCheckoutScript } from "../lumaCheckout";

interface HeroProps {
  event: EventData;
}

interface InfoItem {
  label: string;
  value: string;
  note?: string;
}

const Hero: React.FC<HeroProps> = ({ event }) => {
  useLumaCheckoutScript();
  const lumaEventId = getLumaEventId(event.lumaUrl);

  const info: InfoItem[] = [
    { label: "Date", value: event.date },
    { label: "Time", value: event.time },
    { label: "Venue", value: event.venue },
    { label: "Entry", value: event.entry, note: event.entryNote },
  ];

  return (
    <section className="relative bg-[#F5F7FB] pt-[140px] md:pt-[180px] pb-20 px-4 md:px-6">
      <img
        src={lines}
        alt="Decorative Lines"
        className="absolute top-0 left-0 w-full h-full object-cover pointer-events-none opacity-70"
      />
      <div className="max-w-[960px] mx-auto text-center">
        {/* Badge */}
        <div
          data-aos="fade-down"
          data-aos-delay="100"
          className="inline-flex items-center gap-2 bg-[radial-gradient(108.71%_228.83%_at_-22.65%_12.33%,_rgba(233,_117,_14,_0.2)_0%,_rgba(36,_86,_200,_0.2)_21.97%,_rgba(233,_117,_14,_0.2)_98.44%)] rounded-full px-3 md:px-4 py-2 mb-6"
        >
          <span className="bg-[#D1E9FF] rounded-full py-1 px-2">
            <ArrowRightNew className="w-4 h-4 text-secondary" />
          </span>
          <span className="text-sm font-medium text-neutral-700">
            {event.badge}
          </span>
          <span className="bg-[#D1E9FF] rounded-full py-1 px-2">
            <ArrowRightNew className="w-4 h-4 text-secondary rotate-180" />
          </span>
        </div>

        <h1
          data-aos="fade-up"
          className="text-3xl md:text-4xl lg:text-5xl font-display font-[550] text-[#00003F] leading-tight mb-6 max-w-3xl mx-auto"
        >
          {event.title}
        </h1>

        <p
          data-aos="fade-up"
          data-aos-delay="100"
          className="text-base md:text-lg text-[#475467] max-w-2xl mx-auto mb-10 leading-[1.2]"
        >
          {event.subtitle}
        </p>

        <a
          data-aos="fade-up"
          data-aos-delay="200"
          href={event.lumaUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-luma-action="checkout"
          data-luma-event-id={lumaEventId}
          className="inline-block px-8 py-3 bg-[#012169] hover:bg-[#01154d] text-white font-semibold rounded-lg transition-all duration-200 hover:shadow-lg shadow-md mb-12"
        >
          Reserve my Seat
        </a>

        <div
          data-aos="fade-up"
          data-aos-delay="300"
          className="bg-white rounded-2xl border border-[#EAECF0] shadow-[0px_2px_4px_0px_#00000026] px-8 py-6 max-w-[700px] mx-auto"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-left">
            {info.map((item) => (
              <div
                key={item.label}
                className="border-r border-[#D0D5DD] [&:nth-child(4n)]:border-r-0 md:[&:nth-child(2n)]:border-r [&:nth-child(2n)]:border-r-0"
              >
                <p className="text-sm uppercase tracking-wide text-[#475467] mb-1.5">
                  {item.label}
                </p>
                <p className="text-base font-medium text-[#1D2939]">{item.value}</p>
                {item.note && (
                  <p className="text-xs text-[#667085] mt-0.5">{item.note}</p>
                )}
              </div>
             ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { Hero };
